import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Building2, Mail, Globe, Shield, Palette, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, canViewPage } = useAuth();
  const writable = canWrite(user);
  const canView = canViewPage("settings");
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    api.get("/settings").then((r) => setForm(r.data || {})).finally(() => setLoading(false));
  }, [canView]);

  const save = async () => {
    try {
      await api.put("/settings", form);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const field = (k, label, props = {}) => (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.16em]">{label}</Label>
      <Input value={form[k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} disabled={!writable} data-testid={`settings-${k}`} {...props} />
    </div>
  );

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="module-forbidden">
        <Lock className="w-8 h-8 text-muted-foreground mb-3" />
        <p className="font-display text-lg font-medium">You don't have permission to view this page</p>
      </div>
    );
  }

  if (loading) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">System</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure your KaribuNet workspace.</p>
        </div>
        {writable && (
          <Button onClick={save} className="kn-gradient-bg text-white border-0" data-testid="save-settings-button">
            <Save className="w-4 h-4 mr-2" /> Save Changes
          </Button>
        )}
      </div>

      <Tabs defaultValue="company">
        <TabsList className="bg-muted/40">
          <TabsTrigger value="company"><Building2 className="w-3.5 h-3.5 mr-2" /> Company</TabsTrigger>
          <TabsTrigger value="locale"><Globe className="w-3.5 h-3.5 mr-2" /> Locale</TabsTrigger>
          <TabsTrigger value="comms"><Mail className="w-3.5 h-3.5 mr-2" /> Communications</TabsTrigger>
          <TabsTrigger value="branding"><Palette className="w-3.5 h-3.5 mr-2" /> Branding</TabsTrigger>
          <TabsTrigger value="security"><Shield className="w-3.5 h-3.5 mr-2" /> Security</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <Card className="p-6 rounded-xl border border-border bg-card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field("company_name", "Company Name")}
              {field("email", "Contact Email")}
              {field("phone", "Phone")}
              {field("address", "Address")}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="locale">
          <Card className="p-6 rounded-xl border border-border bg-card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em]">Default Currency</Label>
                <Select value={form.default_currency || "TZS"} onValueChange={(v) => setForm((s) => ({ ...s, default_currency: v }))} disabled={!writable}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["TZS", "USD", "KES", "EUR", "GBP"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em]">Secondary Currency</Label>
                <Select value={form.secondary_currency || "USD"} onValueChange={(v) => setForm((s) => ({ ...s, secondary_currency: v }))} disabled={!writable}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["TZS", "USD", "KES", "EUR", "GBP"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {field("timezone", "Timezone")}
              {field("tax_rate", "Tax Rate (%)", { type: "number" })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="comms">
          <Card className="p-6 rounded-xl border border-border bg-card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field("smtp_host", "SMTP Host")}
              {field("smtp_port", "SMTP Port", { type: "number" })}
              {field("smtp_user", "SMTP Username")}
              {field("smtp_password", "SMTP Password", { type: "password" })}
              {field("sms_provider", "SMS Provider")}
              {field("sms_api_key", "SMS API Key", { type: "password" })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="branding">
          <Card className="p-6 rounded-xl border border-border bg-card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field("logo_url", "Logo URL")}
              {field("primary_color", "Primary Color")}
              {field("tagline", "Tagline")}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card className="p-6 rounded-xl border border-border bg-card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field("session_timeout", "Session Timeout (min)", { type: "number" })}
              {field("password_policy", "Password Policy", { placeholder: "min 8 chars + 1 number" })}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
